import {
  SizeScaleChange,
  SizeScaleChangeStatus,
  SizeScaleDiff,
} from 'types/api/sizeScales.type';

import { plural } from 'helpers/plural';

import { Button } from 'components/Button';
import { Modal } from 'components/Modal';

import * as S from './ImportPreview.styles';

type ImportPreviewProps = {
  fileName: string;
  diff: SizeScaleDiff;
  isApplying: boolean;
  onApply: () => void;
  onCancel: () => void;
};

// Shown in this order; unchanged rows are only counted
const GROUPS: { status: SizeScaleChangeStatus; title: string }[] = [
  { status: 'rejected', title: 'Rejected — not written' },
  { status: 'skipped', title: 'Skipped — not written' },
  {
    status: 'deactivated',
    title: 'Deactivated — in the table, not in the file: can no longer be ordered',
  },
  { status: 'reactivated', title: 'Reactivated — back in the file: can be ordered again' },
  { status: 'added', title: 'Added — new sizes of the scale' },
  { status: 'changed', title: 'Changed — the size gets another Indice (moves in the grid)' },
];

const SUMMARY: SizeScaleChangeStatus[] = [
  'added',
  'changed',
  'reactivated',
  'unchanged',
  'deactivated',
  'skipped',
  'rejected',
];

// the statuses applying writes (deactivated sizes are set inactive, never deleted)
const WRITTEN: SizeScaleChangeStatus[] = ['added', 'changed', 'reactivated', 'deactivated'];

// A size whose Indice changes shows the move: "50 → 30"
const indiceOf = (c: SizeScaleChange) => {
  if (c.sortIndex === null) return c.currentSortIndex ?? '—';
  if (c.currentSortIndex === null || c.currentSortIndex === c.sortIndex) return c.sortIndex;
  return `${c.currentSortIndex} → ${c.sortIndex}`;
};

const orderLinesOf = ({ orderLines, draftOrderLines }: SizeScaleChange) => {
  if (!orderLines) return '—';
  return draftOrderLines ? `${orderLines} (${draftOrderLines} draft)` : orderLines;
};

function ChangeRows({ changes }: { changes: SizeScaleChange[] }): React.ReactElement {
  return (
    <S.Rows>
      <thead>
        <tr>
          <th>Row</th>
          <th>Scale</th>
          <th>Size</th>
          <th>Indice</th>
          <th>Order lines</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((c) => (
          <tr key={`${c.status}-${c.row ?? ''}-${c.scaleCode}-${c.sizeLabel}`}>
            <td>{c.row ?? '—'}</td>
            <td>{c.scaleCode}</td>
            <td>{c.sizeLabel ? `"${c.sizeLabel}"` : '—'}</td>
            <td>{indiceOf(c)}</td>
            <td>{orderLinesOf(c)}</td>
            <td>
              {c.reason}
              {c.warning ? <S.Warning>{c.warning}</S.Warning> : null}
            </td>
          </tr>
        ))}
      </tbody>
    </S.Rows>
  );
}

// What applying deactivates, read before pressing Apply
function Deactivations({ diff }: { diff: SizeScaleDiff }): React.ReactElement | null {
  const sizes = diff.deactivations.flatMap((d) => d.sizes);
  if (sizes.length === 0) return null;

  const lines = sizes.reduce((sum, s) => sum + s.orderLines, 0);
  const drafts = sizes.reduce((sum, s) => sum + s.draftOrderLines, 0);

  return (
    <S.Deactivations data-testid="import-deactivations">
      <strong>
        Applying deactivates {plural(sizes.length, 'size')}: they can no longer be ordered.
        {lines
          ? ` ${plural(
              lines,
              'order line'
            )} hold them (${drafts} in draft orders): they keep them, marked, and can only set them to 0; a draft cannot be sent until it does.`
          : ' No order line holds them.'}
      </strong>
      <ul>
        {diff.deactivations.map(({ scaleCode, sizes: scaleSizes }) => (
          <li key={scaleCode}>
            Scale {scaleCode}:{' '}
            {scaleSizes
              .map(
                (s) =>
                  `"${s.sizeLabel}" (${plural(s.orderLines, 'order line')}, ${
                    s.draftOrderLines
                  } draft)`
              )
              .join(', ')}
          </li>
        ))}
      </ul>
    </S.Deactivations>
  );
}

export function ImportPreview(props: ImportPreviewProps): React.ReactElement {
  const { fileName, diff, isApplying, onApply, onCancel } = props;
  const { summary } = diff;
  const toWrite = WRITTEN.reduce((sum, status) => sum + (summary[status] ?? 0), 0);
  let applyLabel = `Apply ${plural(toWrite, 'change')}`;
  if (toWrite === 0) applyLabel = 'Nothing to apply';
  if (isApplying) applyLabel = 'Applying…';

  return (
    <Modal onClose={onCancel} maxWidth="110rem">
      <S.Container>
        <S.Title>Import preview</S.Title>
        <S.Subtitle>
          {fileName} · sheet &quot;{diff.sheetName}&quot;
        </S.Subtitle>

        <S.Summary data-testid="import-summary">
          {SUMMARY.map((status) => (
            <S.Count key={status} status={status}>
              {status} {summary[status] ?? 0}
            </S.Count>
          ))}
        </S.Summary>

        <S.Groups>
          {GROUPS.map(({ status, title }) => {
            const changes = diff.changes.filter((c) => c.status === status);
            if (changes.length === 0) return null;

            return (
              <section key={status} data-testid={`import-group-${status}`}>
                <S.GroupTitle status={status}>
                  {title} ({changes.length})
                </S.GroupTitle>
                <ChangeRows changes={changes} />
              </section>
            );
          })}

          {summary.unchanged ? (
            <S.Note>
              {summary.unchanged} rows match the table already and stay as they are.
            </S.Note>
          ) : null}
        </S.Groups>

        <Deactivations diff={diff} />

        <S.Buttons>
          <Button
            type="button"
            scale="lg"
            textTransform="uppercase"
            fontFamily="LFT Zeno Condensed"
            motive="filterSecondary"
            disabled={isApplying}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            type="button"
            scale="lg"
            textTransform="uppercase"
            fontFamily="LFT Zeno Condensed"
            motive="primaryBlack"
            disabled={isApplying || toWrite === 0}
            onClick={onApply}
          >
            {applyLabel}
          </Button>
        </S.Buttons>
      </S.Container>
    </Modal>
  );
}
